"use client";

import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

interface BuyersChecklistSectionProps {
  items: string[];
  accentColor?: string;
  title?: string;
  description?: string;
}

export default function BuyersChecklistSection({
  items,
  accentColor = "#2C78B9",
  title = "Key Parameters to Evaluate in Your Next ERP Software",
  description = "Use this checklist to ensure you choose a system that truly fits your business needs.",
}: BuyersChecklistSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-slate-50/50 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50/50 to-white" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-4"
          >
            <CheckCircle2
              className="w-3.5 h-3.5"
              style={{ color: accentColor }}
            />
            <span
              className="text-[10px] font-bold tracking-[0.2em] uppercase"
              style={{ color: accentColor }}
            >
              Buyer&apos;s Checklist
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4 leading-tight"
          >
            {title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed font-medium"
          >
            {description}
          </motion.p>
        </div>

        <div className="bg-white rounded-[2rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.05)] border border-slate-100 p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {items.map((item, idx) => {
              const [label, ...rest] = item.split(":");
              const detail = rest.join(":").trim();

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.5,
                    delay: idx * 0.06,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="group flex items-start gap-4 p-4 rounded-2xl bg-slate-50/50 border border-slate-100 hover:bg-white hover:shadow-md hover:border-slate-200 transition-all duration-300"
                >
                  <div
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: `${accentColor}18` }}
                  >
                    <CheckCircle2
                      className="w-3.5 h-3.5"
                      style={{ color: accentColor }}
                      strokeWidth={3}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className="text-[15px] font-bold text-slate-800 leading-snug block">
                      {label}
                    </span>
                    {detail && (
                      <span className="text-[13px] font-semibold text-slate-500 leading-relaxed mt-1 block">
                        {detail}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
